import { Stack } from "@mui/material";
import { GridSlotsComponentsProps } from "@mui/x-data-grid";
import { DataGridPro, DataGridProProps, GridColDef, GridLogicOperator } from "@mui/x-data-grid-pro";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { useState } from "react";
import useAuth, { DataGridColumns } from "../../context/UserContext";
import { Event } from "../../graphql/types";
import { getFormattedReportDate, toLocalAndUtcDates } from "../../lib/Util";
import IconCopy from "../common/IconCopy";
import { DatagridEmptyState } from "../DatagridEmptyState/DatagridEmptyState";
import { CustomColumnsPanel, customColumnWidthChange, customOnColumnOrderChange } from "../DataGridUtils";
import { HumanDate } from "../Date";
import { EventMessage, parseMessage } from "../EventLogRow/EventLogRow";
import { EventLogStatusIcon } from "../EventLogRow/styles";
import { EventsDrawer } from "./EventsDrawer";

const initialVisibilityModelEventsDatagrid: Partial<Record<DataGridColumns, boolean>> = {
  [DataGridColumns.ReportDateTime]: true,
  [DataGridColumns.Created]: true,
  [DataGridColumns.Severity]: true,
  [DataGridColumns.ProjectName]: true,
  [DataGridColumns.DownloadUpload]: true,
  [DataGridColumns.Message]: true,
};

const EmptyRows = ({ isFiltering }: NonNullable<GridSlotsComponentsProps["noRowsOverlay"]>) => {
  return (
    <DatagridEmptyState
      Icon={InfoIcon}
      text={isFiltering ? "No results, try another filter" : "You have no events yet"}
    />
  );
};

const EventsDataGrid = ({
  rows,
  onRowsScrollEnd,
  loading,
  isFiltering,
}: {
  rows?: Array<Event>;
  onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"];
  loading: boolean;
  isFiltering?: boolean;
}) => {
  const { user, updateDataGridPreferences } = useAuth();
  const [rowSelected, setRowSelected] = useState<null | string>(null);
  const datagridPreferencesElement = user.datagridPreferences.events;
  const columnsWidth = datagridPreferencesElement?.columnsWidth;
  const columns: GridColDef[] = [
    {
      width: columnsWidth?.report_datetime || 170,
      field: "report_datetime",
      headerName: "Report datetime",
      valueFormatter: (_, row: Event) => {
        if (!row.id) {
          return;
        }
        return getFormattedReportDate(row?.report_datetime, true);
      },
      valueGetter: (_value, row) => {
        return getFormattedReportDate(row?.report_datetime, true);
      },
      sortable: false,
    },
    {
      width: columnsWidth?.created || 170,
      field: "created",
      headerName: "Occurred",
      valueFormatter: (_, row: Event) => {
        if (!row.id) {
          return;
        }
        return toLocalAndUtcDates(row?.created).browser;
      },
      renderCell: ({ row }: { row: Event }) => {
        return (
          <Stack direction="row">
            <HumanDate date={row?.created} />
            <IconCopy tooltip={`Event ID: ${row?.id}`} clipboard={`Event ID: ${row?.id}`} />
          </Stack>
        );
      },
      sortable: false,
    },
    {
      width: columnsWidth?.severity || 150,
      minWidth: 150,
      field: "severity",
      headerName: "Severity",
      sortable: false,
      renderCell: ({ row }: { row: Event }) => {
        return <EventLogStatusIcon severity={row?.severity} willRetry={row?.will_retry} />;
      },
    },
    {
      flex: columnsWidth?.project_name ? undefined : 1,
      width: columnsWidth?.project_name,
      field: "project_name",
      headerName: "Project name",
      sortable: false,
    },
    {
      width: columnsWidth?.download_upload || 170,
      field: "download_upload",
      headerName: "Download/Upload",
      sortable: false,
      renderCell: ({ row }: { row: Event }) => {
        if (row?.download_declaration) {
          return row.download_declaration.name;
        }
        if (row?.upload_declaration) {
          return row.upload_declaration.name;
        }
        return "N/A";
      },
    },
    {
      flex: columnsWidth?.message ? undefined : 1,
      width: columnsWidth?.message,
      field: "message",
      headerName: "Message",
      sortable: false,
      renderCell: ({ row }: { row: Event }) => {
        const { longMessage } = parseMessage(row?.message || "");
        return (
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <EventMessage
              file_path={row?.project_name}
              message={longMessage}
              retry_count={row?.retry_count}
              retry_limit={row?.retry_limit}
            />
            <IconCopy tooltip={`${longMessage}`} clipboard={longMessage} />
          </Stack>
        );
      },
    },
  ];

  const columnsCopy = columns.slice();
  const columnsOrder = datagridPreferencesElement?.columnsOrder;
  if (columnsOrder) {
    columnsCopy.sort((a, b) => {
      const aIndex = columnsOrder?.indexOf(a.field) ?? -1;
      const bIndex = columnsOrder?.indexOf(b.field) ?? -1;
      return aIndex - bIndex;
    });
  }

  const handleRowSelected = (id: string) => {
    setRowSelected(id && id === rowSelected ? null : id);
  };
  return (
    <>
      <DataGridPro
        loading={loading}
        rows={rows || []}
        columns={columnsCopy}
        disableColumnFilter
        disableColumnPinning
        disableDensitySelector
        onColumnWidthChange={(column) => customColumnWidthChange(column, "events", user, updateDataGridPreferences)}
        onColumnOrderChange={(column) => {
          customOnColumnOrderChange(column, "events", user, updateDataGridPreferences, columnsCopy);
        }}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
        columnVisibilityModel={
          datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModelEventsDatagrid
        }
        onColumnVisibilityModelChange={(model) => {
          updateDataGridPreferences("events", {
            columnsOrder: columnsOrder,
            columnsWidth: datagridPreferencesElement?.columnsWidth,
            columnVisibilityModel: model,
          });
        }}
        onRowClick={(params) => handleRowSelected(params.row.id)}
        slots={{
          columnsPanel: CustomColumnsPanel,
          noRowsOverlay: EmptyRows,
        }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
          noRowsOverlay: {
            isFiltering: isFiltering,
          },
          row: {
            "data-testid": "upload-row",
            style: {
              cursor: "pointer",
            },
          },

          filterPanel: {
            logicOperators: [GridLogicOperator.And],
          },
          panel: {
            //@ts-ignore
            sx: {
              "& .MuiDataGrid-filterFormValueInput": {
                width: "350px",
                marginLeft: "8px",
                justifyContent: "center",
              },
            },
          },
        }}
        filterMode="server"
        onRowsScrollEnd={onRowsScrollEnd}
        data-testid="events-table"
        initialState={{
          columns: {
            columnVisibilityModel:
              datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModelEventsDatagrid,
          },
        }}
      />
      <EventsDrawer
        event={rows?.find((e) => e.id === rowSelected)}
        open={Boolean(rowSelected)}
        onClose={() => setRowSelected(null)}
      />
    </>
  );
};

export { EventsDataGrid, initialVisibilityModelEventsDatagrid };
